import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import TuneIcon from "@mui/icons-material/Tune";
import { Card, CardContent, Stack, Typography } from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useActiveFBOs } from "../../../containers/ActiveFBOContainer";
import { Hangar, ParamSet } from "../../../types";
import { formatNumber } from "../../../utils";
import { feetToMeters } from "../../../utils/math";
import { getSOPsForLocation } from "../../../utils/sopOverrides";
import { StackStrategy } from "../GenerateParamSets";

type Props = {
  paramSet: ParamSet;
  stackStrategy: StackStrategy;
  setStackStrategy: (stackStrategy: StackStrategy) => void;
  hangar: Hangar;
};

export const SelectStackStrategy: React.FC<Props> = ({
  paramSet,
  stackStrategy,
  setStackStrategy,
  hangar,
}) => {
  const { t } = useTranslation();
  const { activeFBO } = useActiveFBOs();
  const sops = React.useMemo(
    () => (activeFBO ? getSOPsForLocation(activeFBO, hangar) : null),
    [activeFBO, hangar]
  );

  // Helper function to format distance values with proper units
  const formatDistance = (valueInFeet: number | null | undefined): string => {
    if (valueInFeet == null) return t("screens.hangar.selectStackStrategy.common.notAvailable");

    const useMetric = activeFBO?.settings?.useMetricSystem || false;
    if (useMetric) {
      const valueInMeters = feetToMeters(valueInFeet);
      return `${formatNumber(valueInMeters, 1)} m`;
    }
    return `${formatNumber(valueInFeet, 0)} ft`;
  };

  // Helper function to format vertical spacing display
  const formatVerticalSpacing = (overlap: boolean, spacing: number | null | undefined): string => {
    if (!overlap) return t("screens.hangar.selectStackStrategy.common.noOverlap");
    if (spacing == null) return t("screens.hangar.selectStackStrategy.common.notAvailable");
    return formatDistance(spacing);
  };

  return (
    <Stack direction="column">
      <Stack direction="row" spacing={2}>
        <Card
          elevation={0}
          variant="outlined"
          onClick={() => setStackStrategy(StackStrategy.normal)}
          sx={{
            width: "100%",
            borderColor:
              stackStrategy === StackStrategy.normal && "success.main",
            borderWidth: stackStrategy === StackStrategy.normal && 2,
            cursor: "pointer",
          }}
        >
          <CardContent>
            <ThumbUpIcon />
            <Typography>{t("screens.hangar.selectStackStrategy.strategies.normal.title")}</Typography>
            <Typography variant="caption">
              {t("screens.hangar.selectStackStrategy.strategies.normal.description")}
              <br />
              {t("screens.hangar.selectStackStrategy.spacing.horizontal", {
                value: formatDistance(sops?.sop_horizontal_spacing)
              })}
              <br />
              {t("screens.hangar.selectStackStrategy.spacing.overlapMin", {
                value: formatVerticalSpacing(
                  sops?.sop_overlap || false,
                  sops?.sop_vertical_spacing
                )
              })}
            </Typography>
          </CardContent>
        </Card>
        <Card
          elevation={0}
          variant="outlined"
          onClick={() => setStackStrategy(StackStrategy.tight)}
          sx={{
            width: "100%",
            borderColor:
              stackStrategy === StackStrategy.tight && "success.main",
            borderWidth: stackStrategy === StackStrategy.tight && 2,
            cursor: "pointer",
          }}
        >
          <CardContent>
            <Stack direction="row">
              <LocalFireDepartmentIcon />
            </Stack>
            <Typography>{t("screens.hangar.selectStackStrategy.strategies.tight.title")}</Typography>
            <Typography variant="caption">
              {t("screens.hangar.selectStackStrategy.strategies.tight.description")}
              <br />
              {t("screens.hangar.selectStackStrategy.spacing.horizontal", {
                value: formatDistance(
                  sops?.sop_horizontal_spacing_tight ?? paramSet.options.horizontal_spacing
                )
              })}
              <br />
              {t("screens.hangar.selectStackStrategy.spacing.overlapMin", {
                value: formatVerticalSpacing(
                  sops?.sop_overlap_tight || false,
                  sops?.sop_vertical_spacing_tight
                )
              })}
            </Typography>
          </CardContent>
        </Card>
      </Stack>
      <Stack direction="row" spacing={2}>
        <Card
          elevation={0}
          variant="outlined"
          onClick={() => setStackStrategy(StackStrategy.loose)}
          sx={{
            width: "100%",
            borderColor:
              stackStrategy === StackStrategy.loose && "success.main",
            borderWidth: stackStrategy === StackStrategy.loose && 2,
            cursor: "pointer",
          }}
        >
          <CardContent>
            <BeachAccessIcon />
            <Typography>{t("screens.hangar.selectStackStrategy.strategies.loose.title")}</Typography>
            <Typography variant="caption">
              {t("screens.hangar.selectStackStrategy.strategies.loose.description")}
              <br />
              {t("screens.hangar.selectStackStrategy.spacing.horizontal", {
                value: formatDistance(
                  sops?.sop_horizontal_spacing_loose
                )
              })}
              <br />
              {t("screens.hangar.selectStackStrategy.spacing.overlapMin", {
                value: formatVerticalSpacing(
                  sops?.sop_overlap_loose || false,
                  sops?.sop_vertical_spacing_loose
                )
              })}
            </Typography>
          </CardContent>
        </Card>
        <Card
          elevation={0}
          variant="outlined"
          onClick={() => setStackStrategy(StackStrategy.custom)}
          sx={{
            width: "100%",
            borderColor:
              stackStrategy === StackStrategy.custom && "success.main",
            borderWidth: stackStrategy === StackStrategy.custom && 2,
            cursor: "pointer",
          }}
        >
          <CardContent>
            <TuneIcon />
            <Typography>{t("screens.hangar.selectStackStrategy.strategies.custom.title")}</Typography>
            <Typography variant="caption">
              {t("screens.hangar.selectStackStrategy.strategies.custom.description")}
            </Typography>
          </CardContent>
        </Card>
      </Stack>
    </Stack>
  );
};